import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

/**
 * AOCEMS Dispersion Engine product showcase.
 * Renders the live console screenshot inside a cinematic shell with:
 * - Browser chrome + ticking clock + STREAMING pulse
 * - Animated scan line sweeping across the map
 * - SVG particle overlay anchored to the plume axis so the plume looks alive
 * - A pulsing source marker at the stack location
 */

// --- Plume geometry (in image pixel coordinates, viewBox-mapped) ---
// Image is 1878 × 931 after cropping. The bright-green source dot in the
// screenshot sits at (1340, 660). Wind blows toward the upper-left, so the
// plume extends from the source toward (740, 540).
const IMG_W = 1878;
const IMG_H = 931;
const STACK = { x: 1340, y: 660 };
// Vector along the plume (from source toward downwind end) — upper-left
const DIR = { x: -0.978, y: -0.21 };
const PLUME_LEN = 600;
const PARTICLE_COUNT = 42;

const newParticle = () => ({
  age: Math.random(),
  spread: (Math.random() - 0.5) * 0.55,
  speed: 0.85 + Math.random() * 0.45,
});

export default function DispersionEngine() {
  const [now, setNow] = useState(new Date());
  const particlesRef = useRef(
    Array.from({ length: PARTICLE_COUNT }, () => newParticle())
  );
  const [, forceTick] = useState(0);

  // Real ticking clock
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Particle animation loop (rAF, elapsed-time-based)
  useEffect(() => {
    let raf;
    let last = performance.now();
    const loop = (t) => {
      const dt = Math.min(0.06, (t - last) / 1000);
      last = t;
      const arr = particlesRef.current;
      for (let i = 0; i < arr.length; i++) {
        const p = arr[i];
        p.age += dt * 0.18 * p.speed;
        if (p.age > 1) {
          p.age = 0;
          p.spread = (Math.random() - 0.5) * 0.55;
          p.speed = 0.85 + Math.random() * 0.45;
        }
      }
      forceTick((x) => (x + 1) % 100000);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Helpers — perpendicular vector for crosswind spread
  const PERP = { x: -DIR.y, y: DIR.x };
  const sigmaAt = (t) => 8 + 32 * Math.sqrt(t);
  const posAt = (t, perp) => {
    const along = t * PLUME_LEN;
    const cross = perp * sigmaAt(t) * 1.6;
    return {
      x: STACK.x + DIR.x * along + PERP.x * cross,
      y: STACK.y + DIR.y * along + PERP.y * cross,
    };
  };

  const time = now.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const date = now.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return (
    <div
      data-testid="dispersion-engine"
      className="relative bg-zinc-950 rounded-2xl overflow-hidden border border-brand/30 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
    >
      {/* Browser-chrome top bar with streaming indicator */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-800 bg-zinc-950">
        <div className="flex gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
          <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
          <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
        </div>
        <div className="flex-1 flex items-center justify-center gap-2 px-3 py-1 rounded-md bg-zinc-900 text-[10px] font-mono text-zinc-400 max-w-md">
          <span className="material-symbols-outlined text-brand text-[14px]">lock</span>
          aermod-live.africaocems.com
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-brand/40 bg-brand/10">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-brand opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-brand" />
          </span>
          <span className="text-[10px] uppercase tracking-widest text-brand font-bold font-mono">
            Streaming
          </span>
        </div>
      </div>

      {/* Map / screenshot frame with live overlays */}
      <a
        href="/imagery/aocems-live-screen.png"
        target="_blank"
        rel="noreferrer"
        aria-label="Open AOCEMS dispersion console full-resolution screenshot"
        data-testid="aermod-screenshot-link"
        className="block relative group cursor-zoom-in"
      >
        <img
          src="/imagery/aocems-live-screen.png"
          alt="AOCEMS dispersion console — real-time plume isopleths over satellite imagery of Tema with concentration legend showing 25/50/100 µg/m³ thresholds and three receptor sensors active."
          className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.005]"
          loading="lazy"
        />

        {/* SVG particle overlay anchored to the plume axis */}
        <svg
          viewBox={`0 0 ${IMG_W} ${IMG_H}`}
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full pointer-events-none"
          aria-hidden="true"
        >
          {/* Pulsing source at the stack */}
          <circle cx={STACK.x} cy={STACK.y} r="22" fill="#90B849" opacity="0.0">
            <animate
              attributeName="r"
              values="14;46;14"
              dur="2.4s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0.55;0;0.55"
              dur="2.4s"
              repeatCount="indefinite"
            />
          </circle>
          <circle cx={STACK.x} cy={STACK.y} r="9" fill="#90B849" stroke="#fff" strokeWidth="2" />

          {/* Particles drifting downwind with crosswind spread */}
          {particlesRef.current.map((p, i) => {
            const { x, y } = posAt(p.age, p.spread);
            const safeX = Number.isFinite(x) ? x : STACK.x;
            const safeY = Number.isFinite(y) ? y : STACK.y;
            const fade = (1 - p.age) * 0.85;
            const r = 2.5 + p.age * 4;
            // Hue shifts hot-orange → mint as it disperses
            const hue = 22 + p.age * 95;
            return (
              <circle
                key={i}
                cx={safeX}
                cy={safeY}
                r={Number.isFinite(r) ? r : 2.5}
                fill={`hsl(${hue}, 92%, 62%)`}
                opacity={Number.isFinite(fade) ? fade : 0.5}
              />
            );
          })}

          {/* Breathing soft glow on the plume body */}
          <ellipse
            cx={STACK.x + DIR.x * PLUME_LEN * 0.45}
            cy={STACK.y + DIR.y * PLUME_LEN * 0.45}
            rx={PLUME_LEN * 0.5}
            ry={32}
            fill="rgba(250,204,21,0.12)"
            transform={`rotate(${(Math.atan2(DIR.y, DIR.x) * 180) / Math.PI} ${STACK.x + DIR.x * PLUME_LEN * 0.45} ${STACK.y + DIR.y * PLUME_LEN * 0.45})`}
          >
            <animate
              attributeName="opacity"
              values="0.45;0.7;0.45"
              dur="3s"
              repeatCount="indefinite"
            />
          </ellipse>
        </svg>

        {/* Animated scan line — sweeps across once every 7s */}
        <motion.div
          aria-hidden="true"
          className="absolute inset-y-0 w-[2px] pointer-events-none bg-gradient-to-b from-transparent via-brand to-transparent opacity-50"
          animate={{ left: ["-2%", "102%"] }}
          transition={{ duration: 7, repeat: Infinity, ease: "linear" }}
          style={{ filter: "blur(0.5px)" }}
        />
        <motion.div
          aria-hidden="true"
          className="absolute inset-y-0 w-[120px] pointer-events-none"
          animate={{ left: ["-10%", "102%"] }}
          transition={{ duration: 7, repeat: Infinity, ease: "linear" }}
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(144,184,73,0.12) 50%, transparent 100%)",
          }}
        />

        {/* Time-stamped corner overlay */}
        <div className="absolute top-3 right-3 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-black/70 backdrop-blur border border-zinc-800 font-mono">
          <span className="material-symbols-outlined text-brand text-[14px]">schedule</span>
          <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400">
              Live Snapshot
            </div>
            <div className="text-white text-xs font-bold tabular-nums">{time}</div>
            <div className="text-[9px] text-zinc-500">{date}</div>
          </div>
        </div>

        {/* "Open full size" reveal on hover */}
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-950/85 backdrop-blur border border-white/15 text-[10px] uppercase tracking-widest text-white font-bold opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="material-symbols-outlined text-[14px]">open_in_new</span>
          Open full size
        </span>
      </a>

      {/* Live readout strip below screenshot */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-4 py-3 border-t border-zinc-800 bg-zinc-950/80">
        <Stat label="Source" value="SO₂ · 1850 g/s" tone="zinc" />
        <Stat label="Stability Class" value="D · Neutral" tone="zinc" />
        <Stat label="Pts captured" value="219 pts · 24h" tone="zinc" />
        <Stat label="Status" value="ALL OK" tone="brand" />
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const map = {
    red: "text-red-400 border-red-500/40 bg-red-500/10",
    amber: "text-yellow-300 border-yellow-500/40 bg-yellow-500/10",
    brand: "text-brand border-brand/40 bg-brand/10",
    zinc: "text-white border-zinc-800 bg-zinc-900/80",
  };
  return (
    <div
      className={`px-3 py-2 rounded-lg backdrop-blur border ${map[tone]} font-mono`}
    >
      <div className="text-[8px] uppercase tracking-widest opacity-60">{label}</div>
      <div className="text-xs font-bold mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}
